import { useEffect, useRef, useState } from "react";
import * as maplibregl from "maplibre-gl";
import maplibreWorkerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?url";
import type { GeoJSONSource, Map as MapLibreMap, Marker } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import type { Feature, FeatureCollection } from "geojson";
import { getTerrain, getViewport } from "./api";
import { hasUnreadRevision } from "./readState";
import type { ReadState } from "./readState";
import { displayToLegacyZoom, displayToWorld, legacyToDisplayZoom, worldBoundsToDisplay, worldToDisplay } from "./displayCoordinates";
import type { KnowledgeMap, Placement, TerrainFeature, Viewport } from "./types";

maplibregl.setWorkerUrl(maplibreWorkerUrl);

type Camera = { x: number; y: number; z: number };
export type Appearance = "illustrated" | "flat" | "contrast";
type Props = {
  map: KnowledgeMap; selectedPlacementId: string | null;
  onSelect: (placement: Placement) => void;
  onCamera: (camera: Camera) => void; editMode: boolean; refreshKey: number;
  onMove: (placement: Placement, x: number, y: number) => void;
  initialCamera?: Camera | null; focus?: Camera | null;
  appearance?: Appearance;
  readState: ReadState | null;
};
const empty = (): FeatureCollection => ({ type: "FeatureCollection", features: [] });
const geoSource = (map: MapLibreMap, id: string) => map.getSource(id) as GeoJSONSource;

function terrainFeature(feature: Pick<TerrainFeature,"id"|"feature_type"|"geometry"|"style">): Feature | null {
  const points = feature.geometry.points;
  if (!points || points.length < 2) return null;
  const coordinates = points.map(([x,y]) => worldToDisplay({x,y}));
  const geometry = feature.feature_type === "polygon" && points.length >= 3
    ? { type: "Polygon" as const, coordinates: [[...coordinates, coordinates[0]]] }
    : feature.feature_type === "polyline" ? { type: "LineString" as const, coordinates } : null;
  return geometry ? { type: "Feature", id: feature.id, properties: { fill: feature.style.fill ?? "#d4dcc6", stroke: feature.style.stroke ?? "#765c40" }, geometry } : null;
}

export function WorldMap({ map, selectedPlacementId, onSelect, onCamera, editMode, refreshKey, onMove, initialCamera, focus, appearance = "illustrated", readState }: Props) {
  const host = useRef<HTMLDivElement>(null), instance = useRef<MapLibreMap | null>(null);
  const markers = useRef<Marker[]>([]);
  const abort = useRef<AbortController | null>(null), last = useRef<Viewport | null>(null);
  const callbacks = useRef({ onSelect, onCamera, onMove });
  const edit = useRef(editMode), selected = useRef(selectedPlacementId);
  const currentAppearance = useRef(appearance);
  const currentReadState = useRef(readState);
  const refresh = useRef<(() => void) | null>(null);
  const [dense, setDense] = useState(false), [error, setError] = useState("");
  const [layers, setLayers] = useState({ terrain: true, landmarks: true });
  const [showRelationships, setShowRelationships] = useState(() => localStorage.getItem("map-show-relationships") === "true");
  useEffect(() => { callbacks.current = { onSelect, onCamera, onMove }; }, [onSelect, onCamera, onMove]);
  useEffect(() => { edit.current = editMode; if (last.current) drawMarkers(last.current); }, [editMode]);
  useEffect(() => { selected.current = selectedPlacementId; if (last.current) drawMarkers(last.current); }, [selectedPlacementId]);
  useEffect(() => { currentReadState.current = readState; if (last.current) drawMarkers(last.current); }, [readState]);
  useEffect(() => { currentAppearance.current = appearance; applyAppearance(); }, [appearance,layers.terrain]);
  useEffect(() => { refresh.current?.(); }, [refreshKey]);

  function clearMarkers() {
    markers.current.forEach(marker => marker.remove());
    markers.current = [];
  }
  function applyAppearance() {
    const current=instance.current;
    if(!current?.getLayer("generated-tiles"))return;
    const mode=currentAppearance.current;
    if(current.getLayer("generated-tiles"))current.setLayoutProperty("generated-tiles","visibility",layers.terrain&&mode==="illustrated"?"visible":"none");
    current.setPaintProperty("parchment","background-color",mode==="contrast"?"#fff":"#e9e2cf");
    current.setPaintProperty("topic-links-category","line-color",mode==="contrast"?"#111":"#286b83");
    current.setPaintProperty("topic-links-semantic","line-color",mode==="contrast"?"#000":"#a3542d");
  }
  function drawMarkers(data: Viewport) {
    const current = instance.current;
    if (!current?.getSource("terrain")) return;
    clearMarkers();
    const occupied: { x: number; y: number; width: number; height: number }[] = [];
    const ranked = [...data.placements].sort((a, b) =>
      (b.label_priority + (selected.current === b.id ? 10000 : 0))
      - (a.label_priority + (selected.current === a.id ? 10000 : 0))
      || (a.depth ?? 1) - (b.depth ?? 1)
    );
    for (const placement of ranked) {
      if (!placement.topic) continue;
      const pixel = current.project(worldToDisplay(placement));
      const width = placement.depth === 0 ? 116 : 96;
      const height = placement.depth === 0 ? 76 : 64;
      if (occupied.some(other =>
        Math.abs(pixel.x - other.x) < (width + other.width) / 2 + 6 &&
        Math.abs(pixel.y - other.y) < Math.max(height, other.height) + 4
      )) continue;
      occupied.push({ x: pixel.x, y: pixel.y, width, height });
      const element = document.createElement("div"), glyph = document.createElement("span"), label = document.createElement("span");
      const unread = hasUnreadRevision(currentReadState.current, placement.topic_id, placement.topic.updated_at);
      element.className = `atlas-landmark depth-${Math.min(3, placement.depth ?? 1)}${selected.current === placement.id ? " selected" : ""}${edit.current ? " editable" : ""}${unread ? " unread" : ""}`;
      element.setAttribute("role","button"); element.setAttribute("tabindex","0");
      element.setAttribute("aria-label", `${unread ? "New, unread: " : ""}${placement.topic.title} knowledge landmark`);
      element.title = placement.topic.title;
      glyph.className = "atlas-landmark-glyph"; glyph.setAttribute("aria-hidden","true");
      glyph.textContent = placement.role === "secondary" ? "↗" : placement.depth === 0 ? "◆" : placement.icon === "observatory" ? "✦" : "▴";
      label.className = "atlas-landmark-label"; label.setAttribute("aria-hidden","true");
      label.textContent = placement.topic.title;
      element.append(glyph, label);
      element.addEventListener("click", () => callbacks.current.onSelect(placement));
      element.addEventListener("keydown", event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); callbacks.current.onSelect(placement); } });
      const marker = new maplibregl.Marker({element,anchor:"bottom",draggable:edit.current}).setLngLat(worldToDisplay(placement)).addTo(current);
      if (edit.current) marker.on("dragend", () => { const point = displayToWorld(marker.getLngLat()); callbacks.current.onMove(placement,point.x,point.y); });
      markers.current.push(marker);
    }
  }

  useEffect(() => {
    if (!host.current) return;
    const camera = initialCamera ?? map.default_camera;
    const current = new maplibregl.Map({
      container: host.current,
      style: {version:8,sources:{},layers:[{id:"parchment",type:"background",paint:{"background-color":"#e9e2cf"}}]},
      center: worldToDisplay(camera), zoom: legacyToDisplayZoom(camera.z),
      minZoom: legacyToDisplayZoom(map.tiles?.min_zoom ?? -4),
      maxZoom: legacyToDisplayZoom(map.tiles?.max_zoom ?? 4),
      renderWorldCopies:false, attributionControl:false, dragRotate:false, pitchWithRotate:false,
    });
    instance.current = current;
    current.addControl(new maplibregl.NavigationControl({showCompass:false}),"bottom-right");
    if (map.bounds) current.setMaxBounds(worldBoundsToDisplay(map.bounds));
    const fetchViewport = async () => {
      if (!current.getSource("terrain")) return;
      const bounds = current.getBounds(), sw = displayToWorld(bounds.getSouthWest()), ne = displayToWorld(bounds.getNorthEast());
      const zoom = displayToLegacyZoom(current.getZoom());
      abort.current?.abort(); const controller = new AbortController(); abort.current = controller;
      try {
        const worldBounds={min_x:Math.min(sw.x,ne.x),min_y:Math.min(sw.y,ne.y),max_x:Math.max(sw.x,ne.x),max_y:Math.max(sw.y,ne.y)};
        const [data,generated] = await Promise.all([getViewport(map.id,worldBounds,zoom,controller.signal),getTerrain(map.id,worldBounds,zoom,controller.signal)]);
        last.current = data;
        const terrain = generated.features.map(terrainFeature).filter((f):f is Feature=>f!==null);
        geoSource(current,"terrain").setData({type:"FeatureCollection",features:terrain.filter(f=>f.geometry.type==="Polygon")});
        geoSource(current,"topic-links").setData({
          type:"FeatureCollection",
          features:data.links.map(link=>({
            type:"Feature",id:link.id,properties:{kind:link.kind},
            geometry:{type:"LineString",coordinates:[worldToDisplay({x:link.source[0],y:link.source[1]}),worldToDisplay({x:link.target[0],y:link.target[1]})]}
          }))
        });
        drawMarkers(data); setDense(data.has_more); setError("");
        callbacks.current.onCamera({...displayToWorld(current.getCenter()),z:zoom});
      } catch (caught) { if ((caught as Error).name!=="AbortError") setError((caught as Error).message); }
    };
    refresh.current = () => { void fetchViewport(); };
    current.on("load", () => {
      current.addSource("generated-tiles",{type:"raster",tiles:[`/api/v1/maps/${map.id}/tiles/{z}/{x}/{y}.png?v=2`],tileSize:256,minzoom:0,maxzoom:11});
      current.addLayer({id:"generated-tiles",type:"raster",source:"generated-tiles",paint:{"raster-opacity":1,"raster-fade-duration":0}});
      current.addSource("terrain",{type:"geojson",data:empty()});
      current.addLayer({id:"terrain-fill",type:"fill",source:"terrain",paint:{"fill-color":["get","fill"],"fill-opacity":0.3}});
      current.addLayer({id:"terrain-line",type:"line",source:"terrain",paint:{"line-color":["get","stroke"],"line-opacity":0.55,"line-width":1.5}});
      current.addSource("topic-links",{type:"geojson",data:empty()});
      current.addLayer({id:"topic-links-category",type:"line",source:"topic-links",filter:["==",["get","kind"],"category"],layout:{visibility:showRelationships?"visible":"none","line-cap":"round"},paint:{"line-color":"#286b83","line-width":2.5,"line-opacity":0.8}});
      current.addLayer({id:"topic-links-semantic",type:"line",source:"topic-links",filter:["!=",["get","kind"],"category"],layout:{visibility:showRelationships?"visible":"none","line-cap":"round"},paint:{"line-color":"#a3542d","line-width":2.5,"line-opacity":0.85,"line-dasharray":[3,2]}});
      applyAppearance();
      void fetchViewport();
    });
    current.on("moveend",fetchViewport);
    const timer=window.setInterval(()=>{if(!document.hidden)void fetchViewport()},30000);
    window.addEventListener("focus",fetchViewport);
    return ()=>{window.clearInterval(timer);window.removeEventListener("focus",fetchViewport);abort.current?.abort();clearMarkers();current.remove();instance.current=null;last.current=null;refresh.current=null;};
  },[map.id]);

  useEffect(()=>{if(focus&&instance.current)instance.current.easeTo({center:worldToDisplay(focus),zoom:legacyToDisplayZoom(focus.z),duration:window.matchMedia("(prefers-reduced-motion: reduce)").matches?0:500});},[focus]);
  useEffect(()=>{const current=instance.current;if(!current?.getSource("terrain"))return;
    for(const id of ["terrain-fill","terrain-line"])if(current.getLayer(id))current.setLayoutProperty(id,"visibility",layers.terrain?"visible":"none");
    applyAppearance();
    markers.current.forEach(marker=>{marker.getElement().hidden=!layers.landmarks});
  },[layers]);
  useEffect(()=>{const current=instance.current;if(!current?.getSource("topic-links"))return;
    for(const id of ["topic-links-category","topic-links-semantic"])if(current.getLayer(id))current.setLayoutProperty(id,"visibility",showRelationships?"visible":"none");
  },[showRelationships]);
  return <div className="map-wrap" aria-label="Interactive knowledge map"><div ref={host} className="world-map"/>
    <details className="atlas-controls"><summary>Map options</summary><div className="atlas-options">
      <label><input type="checkbox" checked={layers.terrain} onChange={event=>setLayers(value=>({...value,terrain:event.target.checked}))}/>Terrain</label>
      <label><input type="checkbox" checked={layers.landmarks} onChange={event=>setLayers(value=>({...value,landmarks:event.target.checked}))}/>Topics</label>
      <label><input type="checkbox" checked={showRelationships} onChange={event=>{setShowRelationships(event.target.checked);localStorage.setItem("map-show-relationships",String(event.target.checked))}}/>Topic relationships</label>
      {showRelationships&&<small>Solid: categories · dashed: published links</small>}
    </div></details>
    {dense&&<p className="map-notice">Zoom in to reveal more landmarks. Search still finds every published topic.</p>}
    {error&&<p className="map-error" role="alert">{error}</p>}</div>;
}
